import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import { Wheat, Eye, EyeOff, ChevronDown, ArrowLeft, KeyRound, ShieldCheck } from 'lucide-react';

export default function LoginPage() {
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState('admin');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Forgot password states
  const [mode, setMode] = useState('login'); // 'login', 'forgot', 'otp'
  const [forgotEmail, setForgotEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [generatedOtp, setGeneratedOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [success, setSuccess] = useState('');

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(email, password, role);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Login failed. Please check credentials.');
    }
    setLoading(false);
  };

  const handleForgotSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await api.forgotPassword({ email: forgotEmail });
      setGeneratedOtp(res.data.otp);
      setMode('otp');
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Failed to generate OTP');
    }
    setLoading(false);
  };

  const handleOTPSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    setLoading(true);
    try {
      await api.verifyOTP({ email: forgotEmail, otp, newPassword });
      setSuccess('Password reset successfully! You can now login.');
      setTimeout(() => {
        setMode('login');
        setSuccess('');
        setGeneratedOtp('');
        setOtp('');
        setNewPassword('');
        setConfirmPassword('');
      }, 2000);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'OTP verification failed');
    }
    setLoading(false);
  };

  const inputStyle = { background: '#100E0C', border: '1px solid #2A241F', color: '#F5F5F4' };
  const labelStyle = { color: '#C8A96E' };

  return (
    <div className="min-h-screen flex items-center justify-center relative overflow-hidden" style={{ background: '#100E0C' }}>
      <div className="absolute inset-0 opacity-5" style={{
        backgroundImage: `url("https://images.unsplash.com/photo-1609710219183-8d99d74a8f6e?w=1920&q=80")`,
        backgroundSize: 'cover', backgroundPosition: 'center', filter: 'blur(2px)'
      }} />
      <div className="absolute inset-0 bg-gradient-to-br from-[#100E0C] via-[#100E0C]/95 to-[#1E1A17]/90" />

      <div data-testid="login-form" className="relative z-10 w-full max-w-md mx-4 fade-in">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl mb-4" style={{ background: 'rgba(200,169,110,0.1)', border: '1px solid rgba(200,169,110,0.2)' }}>
            <Wheat className="w-8 h-8" style={{ color: '#C8A96E' }} />
          </div>
          <h1 className="text-3xl font-bold tracking-tight" style={{ fontFamily: 'Outfit', color: '#F5F5F4' }}>GrabMillet</h1>
          <p className="mt-2 text-sm" style={{ color: '#A8A29E' }}>Subscription & Delivery Management</p>
        </div>

        <div className="rounded-xl p-8" style={{ background: '#1E1A17', border: '1px solid rgba(200,169,110,0.2)' }}>

          {error && (
            <div data-testid="login-error" className="mb-4 p-3 rounded-lg text-sm" style={{ background: 'rgba(248,113,113,0.1)', border: '1px solid rgba(248,113,113,0.2)', color: '#F87171' }}>
              {error}
            </div>
          )}
          {success && (
            <div data-testid="login-success" className="mb-4 p-3 rounded-lg text-sm" style={{ background: 'rgba(74,222,128,0.1)', border: '1px solid rgba(74,222,128,0.2)', color: '#4ADE80' }}>
              {success}
            </div>
          )}

          {/* ──── LOGIN MODE ──── */}
          {mode === 'login' && (
            <form onSubmit={handleLogin} className="space-y-5">
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider mb-2" style={labelStyle}>Login as</label>
                <div className="relative">
                  <select data-testid="role-select" value={role} onChange={(e) => setRole(e.target.value)}
                    className="w-full px-4 py-3 rounded-lg text-sm appearance-none cursor-pointer" style={inputStyle}>
                    <option value="admin">Admin / CRM Manager</option>
                    <option value="delivery_boy">Delivery Boy</option>
                  </select>
                  <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none" style={{ color: '#A8A29E' }} />
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider mb-2" style={labelStyle}>Email</label>
                <input data-testid="login-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@grabmillet.com" required className="w-full px-4 py-3 rounded-lg text-sm" style={inputStyle} />
              </div>
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider mb-2" style={labelStyle}>Password</label>
                <div className="relative">
                  <input data-testid="login-password" type={showPassword ? 'text' : 'password'} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Enter password" required className="w-full px-4 py-3 pr-12 rounded-lg text-sm" style={inputStyle} />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2" style={{ color: '#A8A29E' }}>
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <button data-testid="login-submit-button" type="submit" disabled={loading} className="w-full py-3 rounded-lg font-semibold text-sm transition-all duration-200 hover:brightness-110 disabled:opacity-50" style={{ background: '#C8A96E', color: '#000' }}>
                {loading ? 'Signing in...' : 'Sign In'}
              </button>
              <button type="button" data-testid="forgot-password-link" onClick={() => { setMode('forgot'); setError(''); }}
                className="w-full text-center text-sm transition-all" style={{ color: '#A8A29E' }}
                onMouseEnter={e => e.target.style.color = '#C8A96E'} onMouseLeave={e => e.target.style.color = '#A8A29E'}>
                Forgot Password?
              </button>
            </form>
          )}

          {/* ──── FORGOT PASSWORD MODE ──── */}
          {mode === 'forgot' && (
            <form onSubmit={handleForgotSubmit} className="space-y-5">
              <button type="button" onClick={() => { setMode('login'); setError(''); }} className="flex items-center gap-1.5 text-sm mb-2" style={{ color: '#A8A29E' }}>
                <ArrowLeft className="w-4 h-4" /> Back to Login
              </button>
              <div className="flex items-center gap-3 mb-2">
                <KeyRound className="w-5 h-5" style={{ color: '#C8A96E' }} />
                <h3 className="text-lg font-semibold" style={{ fontFamily: 'Outfit', color: '#F5F5F4' }}>Reset Password</h3>
              </div>
              <p className="text-sm" style={{ color: '#A8A29E' }}>Enter your email to receive a one-time password (OTP).</p>
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider mb-2" style={labelStyle}>Email</label>
                <input data-testid="forgot-email" type="email" value={forgotEmail} onChange={(e) => setForgotEmail(e.target.value)} placeholder="your registered email" required className="w-full px-4 py-3 rounded-lg text-sm" style={inputStyle} />
              </div>
              <button data-testid="forgot-submit" type="submit" disabled={loading} className="w-full py-3 rounded-lg font-semibold text-sm transition-all duration-200 hover:brightness-110 disabled:opacity-50" style={{ background: '#C8A96E', color: '#000' }}>
                {loading ? 'Generating OTP...' : 'Get OTP'}
              </button>
            </form>
          )}

          {/* ──── OTP VERIFICATION MODE ──── */}
          {mode === 'otp' && (
            <form onSubmit={handleOTPSubmit} className="space-y-5">
              <button type="button" onClick={() => { setMode('forgot'); setError(''); }} className="flex items-center gap-1.5 text-sm mb-2" style={{ color: '#A8A29E' }}>
                <ArrowLeft className="w-4 h-4" /> Back
              </button>
              <div className="flex items-center gap-3 mb-2">
                <ShieldCheck className="w-5 h-5" style={{ color: '#C8A96E' }} />
                <h3 className="text-lg font-semibold" style={{ fontFamily: 'Outfit', color: '#F5F5F4' }}>Verify & Reset</h3>
              </div>

              {/* Show OTP on screen for now */}
              {generatedOtp && (
                <div className="rounded-lg p-4 text-center" style={{ background: 'rgba(200,169,110,0.08)', border: '1px solid rgba(200,169,110,0.2)' }}>
                  <p className="text-xs mb-1" style={{ color: '#A8A29E' }}>Your OTP (valid for 10 minutes)</p>
                  <p data-testid="displayed-otp" className="text-3xl font-bold tracking-[0.3em]" style={{ fontFamily: 'Outfit', color: '#C8A96E' }}>{generatedOtp}</p>
                </div>
              )}

              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider mb-2" style={labelStyle}>Enter OTP</label>
                <input data-testid="otp-input" type="text" maxLength={6} value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))} placeholder="6-digit OTP" required className="w-full px-4 py-3 rounded-lg text-sm text-center tracking-[0.3em]" style={inputStyle} />
              </div>
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider mb-2" style={labelStyle}>New Password</label>
                <input data-testid="new-password" type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder="Min 6 characters" required minLength={6} className="w-full px-4 py-3 rounded-lg text-sm" style={inputStyle} />
              </div>
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider mb-2" style={labelStyle}>Confirm Password</label>
                <input data-testid="confirm-password" type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} placeholder="Re-enter password" required className="w-full px-4 py-3 rounded-lg text-sm" style={inputStyle} />
              </div>
              <button data-testid="otp-submit" type="submit" disabled={loading} className="w-full py-3 rounded-lg font-semibold text-sm transition-all duration-200 hover:brightness-110 disabled:opacity-50" style={{ background: '#C8A96E', color: '#000' }}>
                {loading ? 'Resetting...' : 'Reset Password'}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
